import {Injectable} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {buildPath} from '@utils/util-builder';
import {ApiResponse} from '@models/api.response';
import {
    FianzaEstatusRequest,
    FianzaMasterEstatusRequest,
    FianzaMasterItem,
    FianzaResponse
} from '@models/fianza.model';

@Injectable({
    providedIn: 'root'
})
export class FianzaService {
    private readonly _basePath: string = 'data-core/fianza';

    constructor(private readonly _http: HttpClient) {
    }

    listarPorContrato(contractUuid: string): Observable<FianzaResponse[]> {
        const url: string = buildPath([this._basePath, 'contract', contractUuid]);
        return this._http.get<ApiResponse<FianzaResponse[]>>(url)
            .pipe(map((r: ApiResponse<FianzaResponse[]>): FianzaResponse[] => r.data));
    }

    obtener(uuid: string): Observable<FianzaResponse> {
        const url: string = buildPath([this._basePath, uuid]);
        return this._http.get<ApiResponse<FianzaResponse>>(url)
            .pipe(map((r: ApiResponse<FianzaResponse>): FianzaResponse => r.data));
    }

    cargar(contractUuid: string, formData: FormData): Observable<FianzaResponse> {
        const url: string = buildPath([this._basePath, 'contract', contractUuid]);
        return this._http.post<ApiResponse<FianzaResponse>>(url, formData)
            .pipe(map((r: ApiResponse<FianzaResponse>): FianzaResponse => r.data));
    }

    actualizarEstatus(uuid: string, body: FianzaEstatusRequest): Observable<FianzaResponse> {
        const url: string = buildPath([this._basePath, uuid, 'estatus']);
        return this._http.patch<ApiResponse<FianzaResponse>>(url, body)
            .pipe(map((r: ApiResponse<FianzaResponse>): FianzaResponse => r.data));
    }

    /** Edición completa de una fianza por MASTER (PUT /fianza/{uuid}, multipart, sin restricción de estatus). */
    updateAsMaster(uuid: string, formData: FormData): Observable<FianzaResponse> {
        const url: string = buildPath([this._basePath, uuid]);
        return this._http.put<ApiResponse<FianzaResponse>>(url, formData)
            .pipe(map((r: ApiResponse<FianzaResponse>): FianzaResponse => r.data));
    }

    eliminar(uuid: string): Observable<void> {
        const url: string = buildPath([this._basePath, uuid]);
        return this._http.delete<ApiResponse<void>>(url)
            .pipe(map((): void => void 0));
    }

    descargarArchivo(uuid: string): Observable<Blob> {
        const url: string = buildPath([this._basePath, uuid, 'file']);
        return this._http.get(url, {responseType: 'blob'});
    }

    listarMaster(companiaId: number | null, proveedorId: number | null, estatus: string | null, busqueda: string | null): Observable<FianzaMasterItem[]> {
        let params: HttpParams = new HttpParams();
        if (companiaId != null) { params = params.set('companiaId', companiaId.toString()); }
        if (proveedorId != null) { params = params.set('proveedorId', proveedorId.toString()); }
        if (estatus) { params = params.set('estatus', estatus); }
        if (busqueda?.trim()) { params = params.set('busqueda', busqueda.trim()); }

        const url: string = buildPath([this._basePath]);
        return this._http.get<ApiResponse<FianzaMasterItem[]>>(url, {params})
            .pipe(map((r: ApiResponse<FianzaMasterItem[]>): FianzaMasterItem[] => r.data ?? []));
    }

    actualizarEstatusMaster(uuid: string, body: FianzaMasterEstatusRequest): Observable<FianzaMasterItem> {
        const url: string = buildPath([this._basePath, uuid, 'estatus']);
        return this._http.patch<ApiResponse<FianzaMasterItem>>(url, body)
            .pipe(map((r: ApiResponse<FianzaMasterItem>): FianzaMasterItem => r.data));
    }
}
